import { IDocument, IGetDocumentDownloadResponse, IGetDocumentsResponse, IPostDocumentUploadManyQuery, IPostDocumentUploadManyResponse, IPostDocumentUploadQuery, IPutDocumentQuery, IDocumentProgrammationResponse } from "../../../interfaces/api/IDocuments";
import { IGetWithDisabledPaginationQuery, IGetWithPaginationQuery, IGetWithUuidQuery, IMessageResponse, IMessageWithUuidResponse } from "../../../interfaces/api/IQueries";
import { api } from "../api";
import PutDocumentUploadQuery from "../../../models/api/Documents/PutDocumentUploadQuery";
import PutDocumentProgrammationQuery from "../../../models/api/Documents/PutDocumentProgrammationQuery";

/**
 * it adds the documents endpoint to the api
 * 
 * @returns DocumentsRepository
 */
const DocumentsRepository = api.injectEndpoints({
    endpoints: (build) => ({
        getDocuments: build.query<IGetDocumentsResponse|IMessageResponse, IGetWithPaginationQuery|IGetWithDisabledPaginationQuery>({
            query: (queryParameters) => {
                if("disabled_pagination" in queryParameters) {
                    return {url: `/documents?disabled_pagination=true`, method: "GET"};
                }
                
                if("page" in queryParameters && "per_page" in queryParameters) {
                    return {url: `/documents?page=${queryParameters.page}&per_page=${queryParameters.per_page}`, method: "GET"};
                }

                return {url: "/documents", method: "GET"};
            },          
        }),
        getOneDocument: build.query<IDocument|IMessageResponse, IGetWithUuidQuery>({
            query: (query) => ({url: `/documents/${query.uuid}`, method: "GET"}),
        }),
        getDocumentProgrammation: build.query<IDocumentProgrammationResponse|IMessageResponse, IGetWithUuidQuery>({
            query: (query) => ({url: `/documents/${query.uuid}/programmation`, method: "GET"}),
        }),
        putOneDocument: build.mutation<IMessageResponse, { uuid: string, data: PutDocumentUploadQuery }>({
            query: ({uuid, data}) => ({url: `/documents/${uuid}`, method: "PUT", data}),
        }),
        putOneDocumentProgrammation: build.mutation<IMessageResponse, { uuid: string, data: PutDocumentProgrammationQuery }>({
            query: ({uuid, data}) => ({
                url: `/documents/${uuid}/programmation`,
                method: "PUT",
                data
            }),
        }),
        postDocumentUpload: build.mutation<IMessageWithUuidResponse|IMessageResponse, IPostDocumentUploadQuery>({
            query: (data) => {
                const formData = new FormData();
                formData.append("file", data.file);
                formData.append("description", data.description);
                formData.append("type", data.type);
                data.client_types.forEach(client_type => {
                    formData.append("client_types[]", client_type);
                });

                const isLegalCgv = data.type === 'legal_cgv';
                const hasStartDate = isLegalCgv && typeof data.date_validity_start === 'string';
                const hasEndDate = isLegalCgv && typeof data.date_validity_end === 'string';

                if (hasStartDate && data.date_validity_start) {
                    formData.append("date_validity_start", data.date_validity_start);
                }
                if (hasEndDate && data.date_validity_end) {
                    formData.append("date_validity_end", data.date_validity_end);
                }

                return {
                    url: "/documents/upload", 
                    method: "post",
                    headers: {
                        'Content-Type': 'multipart/form-data;',
                    },
                    data: formData,
                    formData: true,
                };
            },
        }),
        postDocumentUploadMany: build.mutation<IPostDocumentUploadManyResponse|IMessageResponse, IPostDocumentUploadManyQuery>({
            query: (data) => ({url: "/documents/upload/many", method: "POST", data}),
        }),
        getDocumentDownload: build.query<IGetDocumentDownloadResponse, IGetWithUuidQuery>({
            query: (arg) => ({
                url: `/documents/download/${arg.uuid}`, 
                method: "GET",
            }),
        }),
    }),
});

export const { 
    useGetDocumentsQuery,
    useGetOneDocumentQuery,
    usePutOneDocumentMutation,
    usePostDocumentUploadMutation,
    usePostDocumentUploadManyMutation,
    useLazyGetDocumentDownloadQuery,
    useGetDocumentProgrammationQuery,
    useLazyGetDocumentProgrammationQuery,
    usePutOneDocumentProgrammationMutation
} = DocumentsRepository;
